import type { TreatmentLineFilter } from './ongoingTrialsTimeline';

export interface IndicationRef {
  code: string;
  name: string;
}

/** Leading line prefix from emerging_disease_indications.indication_name (e.g. "1L ALK NSCLC"). */
const LINE_PREFIX_RE = /^(\d)L\s+(.+)$/i;

export interface ParsedIndicationLine {
  line: string | null;
  displayBase: string;
}

export function parseIndicationLineName(name: string): ParsedIndicationLine {
  const trimmed = name.trim();
  const match = trimmed.match(LINE_PREFIX_RE);
  if (!match) {
    return { line: null, displayBase: trimmed };
  }
  return { line: match[1], displayBase: match[2].trim() };
}

export interface MergedIndicationOption {
  /** Stable key for selection / URL (`merge:<base>` or raw indication code). */
  key: string;
  displayName: string;
  indicationCodes: string[];
  /** Indication rows whose name starts with `1L `. */
  firstLineCodes: string[];
  /** Indication rows whose name starts with `2L `, `3L `, etc. */
  laterLineCodes: string[];
  hasLineSplit: boolean;
}

export function buildMergedIndicationOptions(
  indications: IndicationRef[],
  indicationCodesWithTrials: Iterable<string>,
): MergedIndicationOption[] {
  const withTrials = new Set(indicationCodesWithTrials);
  const eligible = indications.filter((o) => withTrials.has(o.code));

  const byBase = new Map<string, IndicationRef[]>();
  const standalone: IndicationRef[] = [];

  for (const ind of eligible) {
    const parsed = parseIndicationLineName(ind.name);
    if (parsed.line === null) {
      standalone.push(ind);
      continue;
    }
    const bucket = byBase.get(parsed.displayBase) ?? [];
    bucket.push(ind);
    byBase.set(parsed.displayBase, bucket);
  }

  const merged: MergedIndicationOption[] = [];

  for (const [base, refs] of byBase) {
    const firstLineCodes: string[] = [];
    const laterLineCodes: string[] = [];
    for (const ref of refs) {
      const parsed = parseIndicationLineName(ref.name);
      if (parsed.line === '1') {
        firstLineCodes.push(ref.code);
      } else if (parsed.line != null) {
        laterLineCodes.push(ref.code);
      }
    }
    merged.push({
      key: `merge:${base}`,
      displayName: base,
      indicationCodes: refs.map((r) => r.code),
      firstLineCodes,
      laterLineCodes,
      hasLineSplit: firstLineCodes.length > 0 && laterLineCodes.length > 0,
    });
  }

  for (const ind of standalone) {
    merged.push({
      key: ind.code,
      displayName: ind.name,
      indicationCodes: [ind.code],
      firstLineCodes: [],
      laterLineCodes: [],
      hasLineSplit: false,
    });
  }

  return merged.sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export function resolveMergedIndicationKey(
  rawCodeOrKey: string,
  options: MergedIndicationOption[],
): string | null {
  const trimmed = rawCodeOrKey.trim();
  if (!trimmed) return null;
  const direct = options.find((o) => o.key === trimmed);
  if (direct) return direct.key;
  const parent = options.find((o) => o.indicationCodes.includes(trimmed));
  return parent?.key ?? null;
}

export function mergedIndicationIsFavorited(
  option: MergedIndicationOption,
  favoriteCodes: Set<string>,
): boolean {
  return option.indicationCodes.some((code) => favoriteCodes.has(code));
}

export function trialMatchesTreatmentLineByIndication(
  trialIndicationCode: string | undefined,
  filter: TreatmentLineFilter,
  option: MergedIndicationOption,
): boolean {
  if (filter === 'all') return true;
  if (!trialIndicationCode) return false;
  if (filter === 'untreated') return option.firstLineCodes.includes(trialIndicationCode);
  return option.laterLineCodes.includes(trialIndicationCode);
}

/** Asset / company browse: use each trial's linked indication row names only. */
export function trialMatchesTreatmentLineGlobally(
  trialIndicationCodes: string[] | undefined,
  filter: TreatmentLineFilter,
  indicationNameByCode: Map<string, string>,
): boolean {
  if (filter === 'all') return true;
  const codes = (trialIndicationCodes || []).filter(Boolean);
  if (codes.length === 0) return false;
  for (const code of codes) {
    const name = indicationNameByCode.get(String(code)) || '';
    const parsed = parseIndicationLineName(name);
    if (filter === 'untreated' && parsed.line === '1') return true;
    if (filter === 'previous_treated' && parsed.line != null && parsed.line !== '1') return true;
  }
  return false;
}
